/**
 * Settings landing page (#2425).
 *
 * Replaces a scrolling row of sixteen tabs. Every setting is visible at once,
 * bucketed by what it is: the workshop's own details, the work it does, the
 * space it does it in, and the system around it. A group whose every entry is
 * behind a permission the user lacks is dropped rather than shown empty.
 *
 * The card's header is itself clickable (#2699) and opens the group at its
 * first entry — from there the group's other entries are tabs on the page, so
 * the user browses a card's contents without returning here between topics.
 * Only the header: the entries below are already buttons, and a button inside
 * a button is invalid markup that breaks keyboard navigation.
 */

import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SETTINGS_GROUP_LABEL_KEY, SETTINGS_TAB_META } from './settingsTabMeta';
import { getSettingsGroups, type SettingsTab } from './settingsTabs';

interface Props {
  allowedTabs: SettingsTab[];
  onOpen: (tab: SettingsTab) => void;
}

export function SettingsHome({ allowedTabs, onOpen }: Props) {
  const { t } = useTranslation('settings');
  const groups = getSettingsGroups(allowedTabs);

  return (
    <Box
      data-testid="settings-home"
      // Two columns where there is room, one on a phone. Groups differ in
      // length, so the cards align to the top rather than stretching.
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        alignItems: 'start',
      }}
    >
      {groups.map(({ group, tabs }) => {
        // getSettingsGroups never yields an empty group, so there is always a
        // first entry to land on.
        const firstTab = tabs[0];
        return (
          <Card key={group} variant="outlined">
            <CardActionArea
              onClick={() => { if (firstTab) onOpen(firstTab); }}
              data-testid={`settings-home-group-${group}`}
              aria-label={t(SETTINGS_GROUP_LABEL_KEY[group])}
            >
              <CardContent sx={{ pb: 0 }}>
                <Typography variant="h6" fontWeight={600}>
                  {t(SETTINGS_GROUP_LABEL_KEY[group])}
                </Typography>
              </CardContent>
            </CardActionArea>
            <List disablePadding sx={{ py: 1 }}>
              {tabs.map((tab) => {
                const meta = SETTINGS_TAB_META[tab];
                return (
                  <ListItemButton
                    key={tab}
                    onClick={() => { onOpen(tab); }}
                    data-testid={`settings-home-${tab}`}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                      {meta.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={t(meta.labelKey)}
                      secondary={t(meta.descriptionKey)}
                      slotProps={{
                        primary: { fontWeight: 500 },
                        secondary: { variant: 'body2' },
                      }}
                    />
                    <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </ListItemButton>
                );
              })}
            </List>
          </Card>
        );
      })}
    </Box>
  );
}
